"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useCallback, type CSSProperties } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
interface AndroidKeyboardHookReturn {
  keyboardHeight: number;
  isVisible: boolean;
  isAndroid: boolean;
  getContainerStyle: (baseStyle?: CSSProperties, adjustment?: number) => CSSProperties;
}
export function useAndroidKeyboard() {
  const $ = _c(10);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const isSSR = typeof window === "undefined";
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = !isSSR && Capacitor.getPlatform() === "android";
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const isAndroid = t0;
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (isSSR || !isAndroid) {
        return;
      }
      let mounted = true;
      let showCleanup;
      let hideCleanup;
      const setupKeyboardListeners = async () => {
        if (!Capacitor.isPluginAvailable("Keyboard")) {
          return;
        }
        ;
        try {
          const showListener = await Keyboard.addListener("keyboardWillShow", info => {
            if (!mounted) {
              return;
            }
            const height = info.keyboardHeight ?? 300;
            setKeyboardHeight(height);
            setIsVisible(true);
            document.documentElement.style.setProperty("--android-keyboard-height", `${height}px`);
          });
          const hideListener = await Keyboard.addListener("keyboardWillHide", () => {
            if (!mounted) {
              return;
            }
            setKeyboardHeight(0);
            setIsVisible(false);
            document.documentElement.style.setProperty("--android-keyboard-height", "0px");
          });
          if (!mounted) {
            showListener.remove();
            hideListener.remove();
            return;
          }
          showCleanup = () => showListener.remove();
          hideCleanup = () => hideListener.remove();
        } catch (t3) {
          const error = t3;
          console.error("[Android Keyboard] Error setting up listeners:", error);
        }
      };
      setupKeyboardListeners();
      return () => {
        mounted = false;
        showCleanup?.();
        hideCleanup?.();
        document.documentElement.style.setProperty("--android-keyboard-height", "0px");
      };
    };
    t2 = [isSSR, isAndroid];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== isVisible || $[4] !== keyboardHeight) {
    t3 = (t4, t5) => {
      const baseStyle = t4 === undefined ? {} : t4;
      const adjustment = t5 === undefined ? 40 : t5;
      if (isSSR || !isAndroid || !isVisible || keyboardHeight <= 0) {
        return {
          ...baseStyle,
          transition: baseStyle.transition ?? "transform 0.1s ease-out"
        };
      }
      const adjustedTransform = Math.max(0, keyboardHeight - adjustment);
      const baseTransform = baseStyle.transform ?? "";
      const translateY = adjustedTransform > 0 ? `translateY(-${adjustedTransform}px)` : "";
      const combinedTransform = `${baseTransform} ${translateY}`.trim();
      return {
        ...baseStyle,
        transform: combinedTransform || undefined,
        transition: baseStyle.transition ?? "transform 0.1s ease-out"
      };
    };
    $[3] = isVisible;
    $[4] = keyboardHeight;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const getContainerStyle = t3;
  let t4;
  if ($[6] !== getContainerStyle || $[7] !== isVisible || $[8] !== keyboardHeight) {
    t4 = {
      keyboardHeight,
      isVisible,
      isAndroid,
      getContainerStyle
    };
    $[6] = getContainerStyle;
    $[7] = isVisible;
    $[8] = keyboardHeight;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}